
import React from 'react';

interface StatusConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPublishing?: boolean; // true for publish, false for unpublish (for publish/unpublish mode)
  isDeleting?: boolean; // true for delete/deactivate mode
  isActive?: boolean; // current active status (for delete/deactivate mode)
  eventTitle?: string;
}

const StatusConfirmModal: React.FC<StatusConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isPublishing,
  isDeleting = false,
  isActive = true,
  eventTitle
}) => {
  if (!isOpen) return null;

  // Determine mode: delete/deactivate or publish/unpublish
  const isDeleteMode = isDeleting;
  const actionText = isDeleteMode 
    ? (isActive ? 'deactivate' : 'reactivate')
    : (isPublishing ? 'publish' : 'unpublish');
  const titleText = isDeleteMode
    ? (isActive ? 'Deactivate Event' : 'Reactivate Event')
    : (isPublishing ? 'Publish Event' : 'Unpublish Event');
  const buttonText = isDeleteMode
    ? (isActive ? 'Deactivate' : 'Reactivate')
    : (isPublishing ? 'Publish' : 'Unpublish');
  
  // Color scheme: red for delete/deactivate, green/yellow for publish/unpublish
  const iconBgColor = isDeleteMode 
    ? 'bg-red-100' 
    : (isPublishing ? 'bg-green-100' : 'bg-yellow-100');
  const iconTextColor = isDeleteMode 
    ? 'text-red-600' 
    : (isPublishing ? 'text-green-600' : 'text-yellow-600');
  const titleTextColor = isDeleteMode 
    ? 'text-red-900' 
    : (isPublishing ? 'text-green-900' : 'text-yellow-900');
  const buttonBgColor = isDeleteMode 
    ? 'bg-red-600 hover:bg-red-700' 
    : (isPublishing ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBgColor}`}>
              <svg 
                className={`w-6 h-6 ${iconTextColor}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {isDeleteMode ? (
                  isActive ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  )
                ) : isPublishing ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${titleTextColor}`}>
                {titleText}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Are you sure you want to {actionText} this {isDeleteMode ? 'event' : 'key event'}?
            {eventTitle && (
              <span className="block mt-2 text-sm text-gray-500">
                Event: {eventTitle}
              </span>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${buttonBgColor}`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusConfirmModal;
